// Per-meter notification settings modal — web twin of the mobile
// app/meter/[id]/notifications.tsx screen and the legacy portal's
// notify_settings modal. Five rules (threshold, consumption [sum only],
// no-data, period, alarms), each an enable switch + inputs, saved in the
// legacy stringly-typed shape at app_user.notifications.meters[mid] that the
// `application` notification cron reads. Keep the payload semantics in sync
// with the mobile screen: `sent` re-armed to "0" on every save, `time`
// baselining the look-back window to now − 24h.

import type { ReactElement, ReactNode } from 'react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  getMyMeterNotifications,
  updateMyMeterNotifications,
  type MeterNotifyRules,
} from '../lib/api';
import Icon from './Icon';
import { Modal, Segmented, Spinner, Switch, toast } from './ui';

// One rule: header row with title + enable switch, inputs revealed below
// only while enabled (same reveal behavior as mobile).
function RuleCard({
  title,
  help,
  enabled,
  onToggle,
  children,
}: {
  title: string;
  help: string;
  enabled: boolean;
  onToggle: (v: boolean) => void;
  children?: ReactNode;
}) {
  return (
    <div className="rule-card">
      <div className="rule-head">
        <span className="rule-title">{title}</span>
        <Switch checked={enabled} ariaLabel={title} onChange={onToggle} />
      </div>
      {enabled && (
        <div className="rule-body">
          <span className="rule-help">{help}</span>
          {children}
        </div>
      )}
    </div>
  );
}

function NumberField({
  value,
  onChange,
  unit,
  placeholder,
}: {
  value: string;
  onChange: (v: string) => void;
  unit?: string;
  placeholder: string;
}) {
  return (
    <div className="input-row">
      <input
        type="text"
        inputMode="decimal"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
      />
      {unit && <span className="suffix">{unit}</span>}
    </div>
  );
}

export default function MeterNotifyModal({
  mid,
  pid,
  meterName,
  unit,
  isSum,
  currentValue,
  onClose,
}: {
  mid: string;
  pid: string;
  meterName: string;
  unit: string;
  isSum: boolean;
  /** Formatted last reading, for the "Current value: …" hint. */
  currentValue?: string | null;
  onClose: () => void;
}): ReactElement {
  const { t } = useTranslation();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [alarmNames, setAlarmNames] = useState<string[]>([]);

  // Threshold rule
  const [thEnable, setThEnable] = useState(false);
  const [thDirection, setThDirection] = useState<'0' | '1'>('0');
  const [thValue, setThValue] = useState('');
  // Consumption rule (sum meters only)
  const [coEnable, setCoEnable] = useState(false);
  const [coLength, setCoLength] = useState<'24' | '12'>('24');
  const [coValue, setCoValue] = useState('');
  // No-data / update rule
  const [upEnable, setUpEnable] = useState(false);
  const [upValue, setUpValue] = useState('1');
  const [upLength, setUpLength] = useState<'hour' | 'day'>('hour');
  // Period rule
  const [peEnable, setPeEnable] = useState(false);
  const [peStart, setPeStart] = useState('');
  const [peEnd, setPeEnd] = useState('');
  // Alarms rule
  const [alEnable, setAlEnable] = useState(false);
  const [alSelected, setAlSelected] = useState<string[]>([]);

  // Load the saved rules + available alarm names once, then hydrate the
  // form. Values arrive as strings (the cron's contract); coerce defensively.
  useEffect(() => {
    let cancelled = false;
    getMyMeterNotifications(mid, pid)
      .then((res) => {
        if (cancelled) return;
        setAlarmNames(res.alarm_names ?? []);
        const r = res.rules ?? {};
        if (r.threshold) {
          setThEnable(r.threshold.enable === '1');
          setThDirection(r.threshold.direction === '1' ? '1' : '0');
          setThValue(r.threshold.value != null ? String(r.threshold.value) : '');
        }
        if (r.consumption) {
          setCoEnable(r.consumption.enable === '1');
          setCoLength(r.consumption.length === '12' ? '12' : '24');
          setCoValue(r.consumption.value != null ? String(r.consumption.value) : '');
        }
        if (r.update) {
          setUpEnable(r.update.enable === '1');
          setUpValue(r.update.value != null ? String(r.update.value) : '1');
          setUpLength(r.update.length === 'day' ? 'day' : 'hour');
        }
        if (r.period) {
          setPeEnable(r.period.enable === '1');
          setPeStart(r.period.start ?? '');
          setPeEnd(r.period.end ?? '');
        }
        if (r.selected_alarm) {
          setAlEnable(r.selected_alarm.enable === '1');
          setAlSelected(Array.isArray(r.selected_alarm.list) ? r.selected_alarm.list : []);
        }
      })
      .catch((e: unknown) => {
        if (!cancelled) setError(e instanceof Error ? e.message : t('meter.notify.loadError'));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [mid, pid, t]);

  const toggleAlarm = (name: string) => {
    setAlSelected((prev) =>
      prev.includes(name) ? prev.filter((n) => n !== name) : [...prev, name],
    );
  };

  const onSave = async () => {
    setSaving(true);
    const rules: MeterNotifyRules = {
      time: String(Math.floor(Date.now() / 1000) - 86400),
      threshold: {
        enable: thEnable ? '1' : '0',
        direction: thDirection,
        value: thValue.trim(),
        sent: '0',
      },
      update: {
        enable: upEnable ? '1' : '0',
        value: upValue.trim(),
        length: upLength,
        sent: '0',
      },
      period: {
        enable: peEnable ? '1' : '0',
        start: peStart.trim(),
        end: peEnd.trim(),
        sent: '0',
      },
      selected_alarm: {
        enable: alEnable ? '1' : '0',
        list: alSelected,
      },
    };
    if (isSum) {
      rules.consumption = {
        enable: coEnable ? '1' : '0',
        length: coLength,
        value: coValue.trim(),
        sent: '0',
      };
    }
    try {
      await updateMyMeterNotifications(mid, pid, rules);
      toast(t('meter.notify.saveSuccessMessage'));
      onClose();
    } catch {
      toast(t('meter.notify.saveFailedMessage'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal title={`${t('meter.notify.title')} · ${meterName}`} onClose={onClose}>
      {loading && <Spinner />}
      {!loading && error && <p className="error">{error}</p>}
      {!loading && !error && (
        <>
          <div className="rule-list">
            <RuleCard
              title={t('meter.notify.threshold.title')}
              help={t('meter.notify.threshold.help')}
              enabled={thEnable}
              onToggle={setThEnable}
            >
              <Segmented
                options={[
                  { value: '0' as const, label: t('meter.notify.threshold.above') },
                  { value: '1' as const, label: t('meter.notify.threshold.below') },
                ]}
                value={thDirection}
                onChange={setThDirection}
              />
              <NumberField
                value={thValue}
                onChange={setThValue}
                unit={unit}
                placeholder={t('meter.notify.valuePlaceholder')}
              />
              {currentValue && (
                <span className="rule-help">
                  {t('meter.notify.currentValue', { value: currentValue, unit })}
                </span>
              )}
            </RuleCard>

            {isSum && (
              <RuleCard
                title={t('meter.notify.consumption.title')}
                help={t('meter.notify.consumption.help')}
                enabled={coEnable}
                onToggle={setCoEnable}
              >
                <Segmented
                  options={[
                    { value: '24' as const, label: t('meter.notify.consumption.last24h') },
                    { value: '12' as const, label: t('meter.notify.consumption.last12h') },
                  ]}
                  value={coLength}
                  onChange={setCoLength}
                />
                <NumberField
                  value={coValue}
                  onChange={setCoValue}
                  unit={unit}
                  placeholder={t('meter.notify.valuePlaceholder')}
                />
              </RuleCard>
            )}

            <RuleCard
              title={t('meter.notify.update.title')}
              help={t('meter.notify.update.help')}
              enabled={upEnable}
              onToggle={setUpEnable}
            >
              <div className="rule-row">
                <NumberField
                  value={upValue}
                  onChange={setUpValue}
                  placeholder={t('meter.notify.valuePlaceholder')}
                />
                <Segmented
                  options={[
                    { value: 'hour' as const, label: t('meter.notify.update.hour') },
                    { value: 'day' as const, label: t('meter.notify.update.day') },
                  ]}
                  value={upLength}
                  onChange={setUpLength}
                />
              </div>
            </RuleCard>

            <RuleCard
              title={t('meter.notify.period.title')}
              help={t('meter.notify.period.help')}
              enabled={peEnable}
              onToggle={setPeEnable}
            >
              <div className="rule-row">
                <div className="field">
                  <span className="field-label">{t('meter.notify.period.start')}</span>
                  <div className="input-row">
                    <input
                      type="time"
                      value={peStart}
                      onChange={(e) => setPeStart(e.target.value)}
                      placeholder={t('meter.notify.period.timePlaceholder')}
                    />
                  </div>
                </div>
                <div className="field">
                  <span className="field-label">{t('meter.notify.period.end')}</span>
                  <div className="input-row">
                    <input
                      type="time"
                      value={peEnd}
                      onChange={(e) => setPeEnd(e.target.value)}
                      placeholder={t('meter.notify.period.timePlaceholder')}
                    />
                  </div>
                </div>
              </div>
            </RuleCard>

            <RuleCard
              title={t('meter.notify.alarms.title')}
              help={t('meter.notify.alarms.help')}
              enabled={alEnable}
              onToggle={(v) => {
                // Can't enable when the device exposes no alarms.
                if (v && alarmNames.length === 0) return;
                setAlEnable(v);
              }}
            >
              {alarmNames.length === 0 ? (
                <span className="rule-help">{t('meter.notify.alarms.none')}</span>
              ) : (
                alarmNames.map((name) => {
                  const sel = alSelected.includes(name);
                  return (
                    <button
                      key={name}
                      type="button"
                      className={`alarm-row${sel ? ' selected' : ''}`}
                      onClick={() => toggleAlarm(name)}
                    >
                      <span className="box">{sel && <Icon name="check" size={12} />}</span>
                      {name.replace(/_/g, ' ')}
                    </button>
                  );
                })
              )}
            </RuleCard>
          </div>

          <button
            className="btn btn-primary"
            style={{ width: '100%', justifyContent: 'center', marginTop: 14 }}
            onClick={() => void onSave()}
            disabled={saving}
          >
            <Icon name="check" size={16} />
            {saving ? t('meter.notify.saving') : t('meter.notify.save')}
          </button>
        </>
      )}
    </Modal>
  );
}
